import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardBody, CardFooter, Dialog, Input, Radio, Typography } from "@material-tailwind/react";
import Label from "components/Label/Label";
import { CouponDataSchemaType, Couponschema } from "data/validation";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { EASING } from "../../utils/easingUtils";

// Animation variants
const dialogVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: EASING.easeOut } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2, ease: EASING.easeIn } },
};

const fieldVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASING.easeOut } },
};

interface EntityDialogFormProps {
  open: boolean;
  handleClose?: () => void;
  handleDelete?: () => void;
  isLoading?: boolean;
  addfn?: (payload: any) => Promise<any>;
  addfnTitle?: string;
  updatefnTitle?: string;
}

export const EntityCouponDialogForm: React.FC<EntityDialogFormProps> = React.memo(
  ({ open, handleClose = () => {}, addfn, addfnTitle = "Ajouter un code promo", isLoading = false }) => {
    const {
      register,
      handleSubmit,
      getValues,
      setValue,
      trigger,
      formState: { errors },
      reset,
    } = useForm<CouponDataSchemaType>({
      resolver: zodResolver(Couponschema),
      defaultValues: { percent: undefined, max_redemptions: undefined, number_of_redemptions: undefined },
    });
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const onSubmit = async (data: CouponDataSchemaType) => {
      try {
        setLoading(true);
        if (addfn) {
          const response = await addfn(data);
          if (response?.payload?.status === "OK") {
            toast.success("Code promo ajouté avec succès");
          }
        }
        reset();
        handleClose();
        navigate(0);
      } catch (error: any) {
        toast.error(error.message || "Une erreur est survenue");
      } finally {
        setLoading(false);
      }
    };

    return (
      <AnimatePresence>
        {open && (
          <Dialog
            size="sm"
            open={open}
            handler={handleClose}
            className="w-full max-w-md mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
            aria-labelledby="dialog-title"
          >
            <motion.div variants={dialogVariants} initial="hidden" animate="visible" exit="exit">
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Card className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
                  <CardBody className="flex flex-col gap-6 p-6 max-h-[50vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-t-2xl">
                    <Typography
                      id="dialog-title"
                      variant="h5"
                      className="p-4 font-bold tracking-tight text-center text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl animate-fade-in"
                    >
                      {addfnTitle}
                    </Typography>
                    <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">Réduction (€) :</Label>
                      <div className="relative mt-2">
                        <Input
                          size="lg"
                          type="number"
                          placeholder="exemple : 10"
                          {...register("percent", { setValueAs: (v) => (v === "" ? undefined : parseFloat(v)) })}
                          aria-invalid={!!errors.percent}
                          aria-describedby="percent-error"
                          className="text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                      </div>
                      <AnimatePresence>
                        {errors.percent && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <Typography
                              id="percent-error"
                              variant="small"
                              color="red"
                              className="mt-1 text-xs text-red-500 dark:text-red-400"
                            >
                              {errors.percent.message}
                            </Typography>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                    <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">
                        Nombre d'utilisations :
                      </Label>
                      <div className="flex justify-between mt-2">
                        <Radio
                          color="blue"
                          label={<Typography className="text-gray-700 dark:text-gray-200">Illimité</Typography>}
                          name="max_redemptions"
                          checked={getValues("max_redemptions") === true}
                          onChange={() => {
                            setValue("max_redemptions", true);
                            setValue("number_of_redemptions", 0);
                            trigger("max_redemptions");
                            trigger("number_of_redemptions");
                          }}
                          className="text-blue-500 dark:text-blue-400"
                        />
                        <Radio
                          color="blue"
                          label={<Typography className="text-gray-700 dark:text-gray-200">Limité</Typography>}
                          name="max_redemptions"
                          checked={getValues("max_redemptions") === false}
                          onChange={() => {
                            setValue("max_redemptions", false);
                            trigger("max_redemptions");
                          }}
                          className="text-blue-500 dark:text-blue-400"
                        />
                      </div>
                      <AnimatePresence>
                        {errors.max_redemptions && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <Typography
                              variant="small"
                              color="red"
                              className="mt-1 text-xs text-red-500 dark:text-red-400"
                            >
                              {errors.max_redemptions.message}
                            </Typography>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                    {getValues("max_redemptions") === false && (
                      <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                        <Label className="font-semibold text-gray-700 dark:text-gray-200">Nombre limité :</Label>
                        <div className="relative mt-2">
                          <Input
                            size="lg"
                            type="number"
                            placeholder="exemple : 10"
                            {...register("number_of_redemptions", {
                              setValueAs: (v) => (v === "" ? undefined : parseInt(v)),
                            })}
                            aria-invalid={!!errors.number_of_redemptions}
                            aria-describedby="number-of-redemptions-error"
                            className="text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                          />
                        </div>
                        <AnimatePresence>
                          {errors.number_of_redemptions && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                            >
                              <Typography
                                id="number-of-redemptions-error"
                                variant="small"
                                color="red"
                                className="mt-1 text-xs text-red-500 dark:text-red-400"
                              >
                                {errors.number_of_redemptions.message}
                              </Typography>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    )}
                  </CardBody>
                  <CardFooter className="flex justify-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
                    <ButtonSecondary
                      type="button"
                      onClick={handleClose}
                      className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
                      disabled={loading || isLoading}
                    >
                      Annuler
                    </ButtonSecondary>
                    <ButtonSecondary
                      type="submit"
                      className="px-6 py-2 text-white transition-all duration-300 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 hover:from-blue-600 hover:to-indigo-700 dark:hover:from-blue-700 dark:hover:to-indigo-800 hover:shadow-lg"
                      disabled={loading || isLoading}
                      loading={loading || isLoading}
                    >
                      Confirmer
                    </ButtonSecondary>
                  </CardFooter>
                </Card>
              </form>
            </motion.div>
          </Dialog>
        )}
      </AnimatePresence>
    );
  }
);
